import { TemplateInfo } from "@fartherfinance/frontend/api/TaskWorkflows/Types";
import {
  analyzeField,
  getFieldKey,
} from "@fartherfinance/frontend/api/TaskWorkflows/utils/fieldHelpers";
import { isExpr } from "@fartherfinance/frontend/api/TaskWorkflows/utils/guards";

import { resolveExpressions } from "../expressions/resolveExpressions";

import { ExternalResources } from "./types";

/**
 * Field analysis result containing parameter, key, analysis, and visibility dependencies
 */
export interface FieldAnalysis {
  parameter: TemplateInfo["parameters"][0];
  key: string;
  analysis: ReturnType<typeof analyzeField>;
  visibilityDeps: string[];
}

/**
 * Extracts visibility dependencies from a field parameter by analyzing expressions
 */
function extractVisibilityDependencies(
  param: TemplateInfo["parameters"][0]
): string[] {
  const deps: string[] = [];

  if (
    param.display &&
    "visible" in param.display &&
    Array.isArray(param.display.visible)
  ) {
    param.display.visible.forEach((expr) => {
      if (
        isExpr(expr) &&
        expr.params?.fieldValue?.ref &&
        typeof expr.params.fieldValue.ref === "string"
      ) {
        deps.push(expr.params.fieldValue.ref);
      }
    });
  }

  return deps;
}

/**
 * Analyzes all template parameters and returns field analyses with dependencies
 */
export function analyzeTemplateFields(template: TemplateInfo): FieldAnalysis[] {
  return template.parameters.map((param) => ({
    parameter: param,
    key: getFieldKey(param),
    analysis: analyzeField(param),
    visibilityDeps: extractVisibilityDependencies(param),
  }));
}

/**
 * Collects all unique visibility dependencies from field analyses
 */
export function collectVisibilityDependencies(
  fieldAnalyses: FieldAnalysis[]
): string[] {
  const deps = new Set<string>();

  fieldAnalyses.forEach(({ visibilityDeps }) => {
    visibilityDeps.forEach((dep) => deps.add(dep));
  });

  return Array.from(deps);
}

/**
 * Determines if a field should be visible based on its display configuration
 */
export async function isFieldVisible(
  parameter: TemplateInfo["parameters"][0],
  getValues: (key: string) => unknown,
  externalResources?: ExternalResources
): Promise<boolean> {
  let isVisible = true;

  if (parameter.display && "visible" in parameter.display) {
    // If visible is a boolean, use it directly
    if (typeof parameter.display.visible === "boolean") {
      isVisible = parameter.display.visible;
    }
    // If visible is an array of expressions, evaluate them
    else if (Array.isArray(parameter.display.visible)) {
      try {
        const visibilityResults = await resolveExpressions(
          parameter.display.visible,
          getValues,
          externalResources
        );

        // Field is visible if all expressions evaluate to true
        isVisible = visibilityResults?.every((r) => Boolean(r)) || false;
      } catch (error) {
        // On error, default to hidden (assume falsy)
        isVisible = false;
        console.warn("Error resolving visibility expressions:", error);
      }
    }
  }

  return isVisible;
}
